import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, PiggyBank, Calculator, TrendingUp, ChevronRight } from 'lucide-react';
import { savingsService } from '../services/savings';
import { VD } from '../lib/brand';
import CalculadoraAhorro from './CalculadoraAhorro';

const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

const fecha = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

/**
 * `PANTALLA_AHORRO` — la pestaña de ahorro del cliente.
 *
 * Tiene dos caras y una sola regla para elegir entre ellas: **se enciende
 * cuando hay algo real que enseñar.** Es decir, cuando el usuario ya usó su QR
 * en una tienda, o cuando tiene membresía activa.
 *
 * Antes de eso va en tono tenue y con candados, enseñando la forma de lo que
 * va a ver. No es un adorno: es el gancho: se entiende qué se está perdiendo.
 * Y en esa versión, la tarjeta central invita a calcular cuánto ahorraría, y
 * lleva a la calculadora que ya vive en el landing.
 */
export default function MiAhorro() {
  const navigate = useNavigate();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);

  useEffect(() => {
    let vigente = true;
    savingsService
      .summary()
      .then((r) => vigente && setDatos(r?.data ?? null))
      .catch(() => {})
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, []);

  /* La regla completa: encendida si ya canjeó algo o si paga. */
  const encendida = Boolean(datos?.hasData || datos?.isSubscriber);

  /* En la versión apagada todo se ve, pero atenuado: la forma sí, los números no. */
  const tenue = encendida ? {} : { opacity: 0.45, filter: 'saturate(0.5)' };

  if (detalle) {
    return <DetalleAhorro movimiento={detalle} onVolver={() => setDetalle(null)} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fff', paddingBottom: 120 }}>
      <div style={{ padding: 'max(66px, env(safe-area-inset-top, 66px)) 24px 0' }}>
        <h1
          className="font-display"
          style={{
            fontSize: 'clamp(34px, 10vw, 44px)',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: '#0E0E10',
            margin: '0 0 22px',
          }}
        >
          Mi ahorro
        </h1>

        {/* ── Ahorro acumulado ─────────────────────────────────────────── */}
        <Tarjeta>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0, ...tenue }}>
              <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#33333A' }}>
                Tu ahorro acumulado
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0 4px' }}>
                <span
                  className="font-display"
                  style={{
                    fontSize: 'clamp(32px, 10vw, 42px)',
                    fontWeight: 700,
                    letterSpacing: '-0.03em',
                    color: '#1A9C52',
                    lineHeight: 1,
                  }}
                >
                  {cargando ? '—' : pesos(datos?.total)}
                </span>
                {!encendida && <Lock size={18} color="#6E6E77" aria-hidden="true" />}
              </div>
              <p style={{ margin: 0, fontSize: 12.5, color: '#6E6E77' }}>
                {datos?.equivalence?.access?.label
                  ? `equivale a ${datos.equivalence.access.label}`
                  : 'aquí verás a cuánto acceso equivale'}
              </p>
            </div>
            <PiggyBank size={64} color="#1A9C52" style={{ flex: 'none', opacity: encendida ? 0.9 : 0.35 }} />
          </div>
        </Tarjeta>

        {/* ── La tarjeta que cambia según el estado ────────────────────── */}
        {encendida ? (
          <TarjetaCelebracion datos={datos} />
        ) : (
          /*
            La calculadora vive aquí, no en el landing.

            Antes esta tarjeta mandaba a `/?calculadora=1`: sacaba al usuario de
            su app, a una página de venta, y luego tenía que volver solo. Iván lo
            pidió explícito. Es el mismo componente que usa el landing, no una
            copia.
          */
          <div
            style={{
              background: 'linear-gradient(100deg, #F73E48, #FA759F)',
              borderRadius: 22,
              padding: '18px 18px 20px',
              margin: '14px 0',
              boxShadow: '0 12px 30px -14px rgba(247,62,72,0.6)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
              <Calculator size={38} color="#fff" style={{ flex: 'none', opacity: 0.9 }} />
              <div>
                <div className="font-display" style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>
                  ¡Calcula tu ahorro!
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.92)', lineHeight: 1.4, marginTop: 2 }}>
                  Mira cuánto te ahorrarías con vondi
                </div>
              </div>
            </div>
            <CalculadoraAhorro compacta />
          </div>
        )}

        {/* ── Este mes ─────────────────────────────────────────────────── */}
        <Tarjeta>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, ...tenue }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 14, color: '#6E6E77' }}>este mes has ahorrado</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span
                  className="font-display"
                  style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.03em', color: '#F73E48' }}
                >
                  {cargando ? '—' : pesos(datos?.thisMonth)}
                </span>
                {!encendida && <Lock size={16} color="#6E6E77" aria-hidden="true" />}
              </div>
            </div>
            <TrendingUp size={34} color="#F73E48" style={{ flex: 'none', opacity: 0.7 }} />
            <div
              style={{
                borderLeft: '1px solid #E7E7EC',
                paddingLeft: 14,
                textAlign: 'center',
                flex: 'none',
              }}
            >
              <div style={{ fontSize: 12.5, color: '#6E6E77' }}>compras</div>
              <div className="font-display" style={{ fontSize: 26, fontWeight: 700, color: '#0E0E10' }}>
                {cargando ? '—' : datos?.purchasesThisMonth ?? 0}
              </div>
              <div style={{ fontSize: 11, color: '#6E6E77' }}>este mes</div>
            </div>
          </div>
        </Tarjeta>

        {/* ── Historial ────────────────────────────────────────────────── */}
        <div style={{ marginTop: 26 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
            <h2 className="font-display" style={{ fontSize: 19, fontWeight: 600, color: '#0E0E10', margin: 0 }}>
              Historial de ahorros
            </h2>
          </div>

          {!cargando && (datos?.history?.length ?? 0) === 0 && (
            <p style={{ fontSize: 13.5, color: '#6E6E77', lineHeight: 1.5, margin: 0 }}>
              Todavía no tienes movimientos. En cuanto uses un beneficio en tienda, cada
              compra aparecerá aquí con lo que te ahorraste.
            </p>
          )}

          <div style={{ borderRadius: 22, overflow: 'hidden', border: '1px solid #E7E7EC' }}>
            {(datos?.history ?? []).map((h, i) => (
              <button
                key={h.id}
                type="button"
                onClick={() => setDetalle(h)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 16px',
                  background: '#fff',
                  border: 'none',
                  borderTop: i === 0 ? 'none' : '1px solid #E7E7EC',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    background: 'linear-gradient(100deg, #FA4D0A, #F73E48)',
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {(h.storeName || '?').slice(0, 2).toUpperCase()}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0E0E10' }}>{h.storeName}</div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: '#6E6E77',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h.promotion}
                  </div>
                  <div style={{ fontSize: 11.5, color: VD.pizarra }}>{fecha(h.date)}</div>
                </div>
                {/*
                  Lo que se llevó, no siempre una cifra.

                  Unos meses sin intereses no son un ahorro en pesos, y poner
                  «$0» los haría parecer un movimiento fallido. En su lugar se
                  nombra el beneficio, que es lo que de verdad se usó ese día.
                */}
                {h.modo === 'en_especie' ? (
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: '#F73E48',
                      flex: 'none',
                      maxWidth: 118,
                      textAlign: 'right',
                      lineHeight: 1.3,
                    }}
                  >
                    {h.etiqueta || h.promotion}
                  </div>
                ) : (
                  <div
                    className="font-display"
                    style={{ fontSize: 17, fontWeight: 700, color: '#F73E48', flex: 'none' }}
                  >
                    {h.saved === null ? '—' : pesos(h.saved)}
                  </div>
                )}
                <ChevronRight size={18} color={VD.pizarra} style={{ flex: 'none' }} />
              </button>
            ))}
          </div>

          {/* Los canjes anteriores al 11-ago-2026 no traen monto: nadie se lo pedía
              al cajero. Se avisa en vez de enseñar un guion sin explicación. */}
          {(datos?.history ?? []).some((h) => h.saved === null) && (
            <p style={{ fontSize: 11.5, color: VD.pizarra, marginTop: 10, lineHeight: 1.45 }}>
              Los movimientos con «—» son anteriores a que empezáramos a registrar el monto
              de cada compra.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Tarjeta({ children }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E7E7EC',
        borderRadius: 22,
        padding: '18px 20px',
        marginBottom: 14,
        boxShadow: '0 10px 30px -18px rgba(14,14,16,0.25)',
      }}
    >
      {children}
    </div>
  );
}

/**
 * La tarjeta de celebración del mockup: cuántos años de acceso lleva pagados
 * con lo que se ha ahorrado, con una marca por año.
 */
function TarjetaCelebracion({ datos }) {
  const anios = Math.floor(datos?.equivalence?.access?.years ?? 0);
  const desde = new Date().getFullYear();
  const marcas = Array.from({ length: Math.min(anios, 7) }, (_, i) => desde + i);

  return (
    <div
      style={{
        background: 'linear-gradient(100deg, #F73E48, #FA759F)',
        borderRadius: 22,
        padding: '18px 20px',
        margin: '14px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        boxShadow: '0 12px 30px -14px rgba(247,62,72,0.6)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="font-display" style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>
          {anios >= 1 ? '¡Excelente!' : '¡Vas muy bien!'}
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.92)', lineHeight: 1.4, marginTop: 4 }}>
          {anios >= 1
            ? `Llevas ahorrado ${anios === 1 ? 'un año' : `${anios} años`} de acceso a vondi`
            : `Llevas ${datos?.equivalence?.access?.label ?? 'ahorro'} acumulado`}
        </div>
      </div>

      {marcas.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, auto)',
            gap: 6,
            flex: 'none',
          }}
        >
          {marcas.map((a) => (
            <div key={a} style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 9999,
                  border: '1.5px solid rgba(255,255,255,0.9)',
                  color: '#fff',
                  fontSize: 12,
                  display: 'grid',
                  placeItems: 'center',
                  margin: '0 auto',
                }}
              >
                ✓
              </div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>{a}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * `DETALLE_AHORRO` — el desglose de un movimiento.
 */
function DetalleAhorro({ movimiento, onVolver }) {
  const total = movimiento.ticketTotal;
  const ahorro = movimiento.saved;

  return (
    <div style={{ minHeight: '100vh', background: '#fff', paddingBottom: 120 }}>
      <div style={{ padding: 'max(66px, env(safe-area-inset-top, 66px)) 24px 0' }}>
        <button
          type="button"
          onClick={onVolver}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#6E6E77',
            fontSize: 13.5,
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
            marginBottom: 14,
          }}
        >
          ← Volver
        </button>

        <h1
          className="font-display"
          style={{ fontSize: 26, fontWeight: 700, color: '#0E0E10', margin: '0 0 20px', textAlign: 'center' }}
        >
          Detalles de ahorro
        </h1>

        <div
          style={{
            border: '1px solid #E7E7EC',
            borderRadius: 26,
            padding: '26px 22px',
            boxShadow: '0 12px 34px -18px rgba(14,14,16,0.3)',
          }}
        >
          <div
            className="font-display"
            style={{ fontSize: 30, fontWeight: 700, color: '#0E0E10', textAlign: 'center', letterSpacing: '-0.02em' }}
          >
            {movimiento.storeName}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#33333A', textAlign: 'center', marginTop: 8 }}>
            {movimiento.promotion}
          </div>
          <div style={{ fontSize: 12.5, color: '#6E6E77', textAlign: 'center', marginTop: 4 }}>
            {fecha(movimiento.date)}
          </div>

          <div style={{ marginTop: 30, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Renglon etiqueta="Total de compra" valor={total === null ? '—' : pesos(total)} />
            <Renglon
              etiqueta="Descuento vondi aplicado"
              valor={ahorro === null ? '—' : `-${pesos(ahorro)}`}
              color="#1A9C52"
            />
            {movimiento.ticketNumber && (
              <Renglon etiqueta="Número de ticket" valor={movimiento.ticketNumber} color="#6E6E77" />
            )}
          </div>

          <div style={{ borderTop: '2px solid #0E0E10', margin: '18px 0 0', paddingTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="font-display" style={{ fontSize: 26, fontWeight: 700, color: '#0E0E10' }}>
                Ahorraste
              </span>
              <span
                className="font-display"
                style={{
                  fontSize: 30,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  backgroundImage: 'linear-gradient(100deg, #F52B16, #FA759F)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                {ahorro === null ? '—' : pesos(ahorro)}
              </span>
            </div>
          </div>

          {ahorro === null && (
            <p style={{ fontSize: 11.5, color: VD.pizarra, marginTop: 14, lineHeight: 1.45 }}>
              Este canje es anterior a que empezáramos a registrar el monto de cada compra,
              por eso no tiene cifras.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Renglon({ etiqueta, valor, color = '#0E0E10' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: 13.5, fontWeight: 600, color: '#33333A' }}>{etiqueta}</span>
      <span className="font-display" style={{ fontSize: 16, fontWeight: 700, color }}>
        {valor}
      </span>
    </div>
  );
}
